<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import coffeeIcon from "@core/assets/icons/coffee-small.svg?raw";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import { fathersFamilyDiscountProductAmount } from "@core/schema/fathersFamilyDiscount.js";
	import { getCustomerType } from "@core/schema/utils/getCustomerType.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./CartClosestBenefit.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	interface Benefit {
		text: string;
		progress?: number;
		onclick?(this: void): void;
	}

	const { subcategories } = getGlobalTemplateProps();

	const eligibleSubcategories = $derived(subcategories.filter(({ eligibleForB2cDiscount }) => eligibleForB2cDiscount));
	const eligibleOrderProductsInCart = $derived(
		Cart.orderProducts.filter((orderProduct) => {
			const product = Cart.findProduct(orderProduct);
			if (!product) {
				return false;
			}

			return eligibleSubcategories.some(({ id }) => id === product.subcategoryId);
		}),
	);
	const eligibleProductCountInCart = $derived(
		eligibleOrderProductsInCart.reduce((acc, curr) => acc + curr.quantity, 0),
	);

	const nextBenefit: Benefit | null = $derived.by(() => {
		if (!Auth.signedIn) {
			return {
				text: translate("registerForFreeShipping"),
				onclick(): void {
					Auth.openAuthenticationModal(AuthenticationModalKind.Register, {});
				},
			};
		}

		if (getCustomerType(Auth.loggedCustomer?.pricingLevel) !== CustomerType.B2c) {
			return null;
		}

		const missing = Math.max(0, fathersFamilyDiscountProductAmount - eligibleProductCountInCart);
		if (missing <= 0) {
			return null;
		}

		return {
			text: missing === 1 ? translate("coffeDiscountAddOne") : translate("coffeDiscountAddMore", { missing }),
			progress: eligibleProductCountInCart / fathersFamilyDiscountProductAmount,
		};
	});
</script>

{#if nextBenefit}
	<div class="mt-1 2xl:mt-2 flex items-center gap-4 bg-white p-3 2xl:p-4 rounded-2xl 2xl:rounded-3xl">
		<div
			class="size-12 2xl:size-14 bg-fcrbrown text-transparent flex items-center justify-center rounded-full shrink-0 grow-0"
		>
			<Icon icon={coffeeIcon} class="size-6 text-white" />
		</div>
		<div class="grow flex flex-col gap-2">
			<div
				class="text-fcrdarkgray font-normal text-4/tighter w-full h-fit flex flex-col md:flex-row justify-between pr-2 gap-2 md:items-center"
			>
				<Formatted text={nextBenefit.text} />
				{#if nextBenefit.onclick}
					<button
						type="button"
						class="underline font-medium text-4/tighter md:text-lg text-left md:text-right"
						onclick={nextBenefit.onclick}
					>
						{translate("register")}
					</button>
				{/if}
			</div>
			{#if nextBenefit.progress !== undefined}
				<div class="rounded-full bg-white w-full">
					<div class="rounded-full bg-fcrdarkgray h-2 w-1/2" style="width: {nextBenefit.progress * 100}%;"></div>
				</div>
			{/if}
		</div>
	</div>
{/if}
